import { useEffect } from "react";
import { Button, Input, Stack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { notify } from "@/ui/chakra/notify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetWorkOrder } from "../useCases/useGetWorkOrder";
import { useUpdateWorkOrder } from "../useCases/useUpdateWorkOrder";
import { workOrderSchema } from "../schemas/workOrderSchema";
import { useAuth } from "@/shared/session";
import { useGetClientEntities } from "@/core/clientEntities/useCases/useGetClientEntities";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { NativeSelectField, NativeSelectRoot } from "@/ui/chakra/native-select";

type WorkOrderFormInput = z.infer<typeof workOrderSchema>;

// TODO: Modify workOrderId to be a workOrder object.
export interface UpdateWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
}

export const UpdateWorkOrderModal = ({
  isOpen,
  onClose,
  workOrderId,
}: UpdateWorkOrderModalProps) => {
  const { user } = useAuth();

  const { zones } = user || {};

  const { data: workOrders = [] } = useGetWorkOrder();
  const { isPending, mutate: updateWorkOrder } = useUpdateWorkOrder();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm<WorkOrderFormInput>({
    resolver: zodResolver(workOrderSchema),
    defaultValues: {
      name: "",
      code: "",
      zoneId: "",
      clientEntityId: "",
      enabled: true,
    },
  });

  const zoneId = watch("zoneId");

  // Client entities belong to zones, so only offer the ones of the chosen zone.
  const { data: clientEntities, isPending: isClientEntitiesLoading } =
    useGetClientEntities({ zoneIds: zoneId || undefined });

  useEffect(() => {
    if (!isOpen) return;

    const selectedWorkOrder = workOrders.find(
      (workOrder) => workOrder.id === workOrderId,
    );

    if (!selectedWorkOrder) return;

    setValue("name", selectedWorkOrder.name);
    setValue("code", selectedWorkOrder.code);
    setValue("zoneId", selectedWorkOrder.zone.id);
    setValue("clientEntityId", selectedWorkOrder.clientEntity.id);
    setValue("enabled", selectedWorkOrder.enabled);
  }, [isOpen, setValue, workOrders, workOrderId]);

  useEffect(() => {
    const clientEntityId = getValues("clientEntityId");
    if (
      clientEntityId &&
      !isClientEntitiesLoading &&
      !clientEntities.some((clientEntity) => clientEntity.id === clientEntityId)
    )
      setValue("clientEntityId", "");
  }, [clientEntities, isClientEntitiesLoading, getValues, setValue]);

  const onFormSubmit = (data: WorkOrderFormInput) => {
    updateWorkOrder(
      { id: workOrderId, updatedWorkOrder: data },
      {
        onSuccess: () => {
          notify.success("Orden de trabajo actualizada");
          onClose();
        },
        onError: (error) =>
          notify.error("No se pudo actualizar la orden de trabajo", error),
      },
    );
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Editar orden de trabajo</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              <Field
                label="Nombre"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register("name")}
                  placeholder="Nombre"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Código"
                required
                invalid={!!errors.code}
                errorText={errors.code?.message}
              >
                <Input
                  {...register("code")}
                  placeholder="Código"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Zona"
                required
                invalid={!!errors.zoneId}
                errorText={errors.zoneId?.message}
              >
                <NativeSelectRoot>
                  <NativeSelectField
                    {...register("zoneId")}
                    placeholder="Seleccione una zona"
                  >
                    {zones?.map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
              <Field
                label="Empresa"
                required
                invalid={!!errors.clientEntityId}
                errorText={errors.clientEntityId?.message}
              >
                <NativeSelectRoot disabled={!zoneId || isClientEntitiesLoading}>
                  <NativeSelectField
                    {...register("clientEntityId")}
                    placeholder="Seleccione una empresa"
                  >
                    {clientEntities?.map((clientEntity) => (
                      <option key={clientEntity.id} value={clientEntity.id}>
                        {clientEntity.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Stack>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
